import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Users2, Merge, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MATCH = { nbd: "nome + cognome + data di nascita (duplicato probabile)", email: "stessa email — attenzione: spesso fratelli/sorelle con email del genitore", phone: "stesso telefono — attenzione: spesso fratelli/sorelle", nn: "nome + cognome (data nascita mancante/diversa)" };
const STRONG = new Set(["nbd", "cf"]);

export default function DuplicatesDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [clusters, setClusters] = useState(null);
  const [primary, setPrimary] = useState({});
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("Unione schede duplicate (stesso atleta)");
  const [busy, setBusy] = useState(false);

  const load = () => { setPreview(null); api.get("/athletes-duplicates").then((r) => { setClusters(r.data); const p = {}; r.data.forEach((c) => { p[c.key] = c.suggested_primary_id; }); setPrimary(p); }); };
  useEffect(() => { if (open) load(); }, [open]);

  const doPreview = async (c) => {
    setBusy(true);
    try {
      const pid = primary[c.key]; const dups = c.athletes.map((a) => a.id).filter((id) => id !== pid);
      const { data } = await api.post("/athletes/merge/preview", { primary_id: pid, duplicate_ids: dups });
      setPreview({ ...data, cluster: c, pid, dups });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doMerge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/merge", { primary_id: preview.pid, duplicate_ids: preview.dups, reason, preview_id: preview.preview_id, expected_primary_version: preview.expected_primary_version });
      toast.success(`Schede unite: ${preview.dups.length} duplicati archiviati${data.merge_id ? ` · annullabile per ${data.undo_window_seconds || 30}s` : ""}`);
      load(); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="duplicates-btn" variant="outline" className="h-12" onClick={() => setOpen(true)}><Users2 className="w-4 h-4 mr-2" />Duplicati{clusters?.length ? ` (${clusters.length})` : ""}</Button>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5" />Schede atleta duplicate</DialogTitle>
          <DialogDescription>Scegli la scheda principale: presenze, test, lezioni, armi, gruppi e gare dei doppioni vengono trasferiti su di essa e i doppioni archiviati (mai cancellati, annullabile).</DialogDescription>
        </DialogHeader>
        {!clusters && <div className="text-sm text-zinc-500 py-6 text-center">Analisi…</div>}
        {clusters?.length === 0 && <div className="text-sm text-emerald-700 py-6 text-center" data-testid="duplicates-none">Nessun duplicato rilevato tra gli atleti attivi.</div>}
        {!preview && clusters?.map((c) => (
          <div key={c.key} className="border border-zinc-200 rounded-md p-3 space-y-2" data-testid={`dup-cluster-${c.key}`}>
            <div className={`text-[10px] uppercase tracking-wider ${STRONG.has(c.match_type) ? "text-zinc-500" : "text-amber-700"}`}>Corrispondenza: {MATCH[c.match_type] || c.match_type}</div>
            {c.athletes.map((a) => (
              <label key={a.id} className="flex items-center gap-3 text-sm cursor-pointer">
                <input type="radio" name={`p-${c.key}`} data-testid={`dup-primary-${a.id}`} checked={primary[c.key] === a.id} onChange={() => setPrimary({ ...primary, [c.key]: a.id })} />
                <span className="font-medium">{(a.last_name || "").toUpperCase()} {a.first_name}</span>
                <span className="font-mono text-xs text-zinc-500">{a.birth_date || "—"} · {a.fiscal_code || "no CF"} · creato {(a.created_at || "").slice(0, 10)}{a.golee_id ? " · Golee" : ""}</span>
                {primary[c.key] === a.id && <span className="text-[10px] uppercase bg-black text-white px-1.5 py-0.5 rounded">principale</span>}
              </label>
            ))}
            <Button size="sm" data-testid={`dup-preview-${c.key}`} disabled={busy} onClick={() => doPreview(c)} className="bg-black hover:bg-zinc-800">Anteprima unione</Button>
          </div>
        ))}
        {preview && (
          <div className="space-y-3" data-testid="merge-preview">
            <div className="text-sm">Principale: <strong>{(preview.primary.last_name || "").toUpperCase()} {preview.primary.first_name}</strong> · da archiviare: {preview.dups.length}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {preview.transfers.filter((t) => t.count).map((t) => <div key={t.collection} className="border border-zinc-200 rounded p-2"><div className="text-zinc-500">{t.collection}</div><div className="font-mono font-bold text-base">{t.count}</div></div>)}
              {preview.transfers.every((t) => !t.count) && <div className="text-zinc-500 col-span-4">Nessun record dipendente da trasferire.</div>}
            </div>
            {preview.field_changes?.length > 0 && <div className="text-xs text-zinc-600">Campi completati dalla scheda doppione: {preview.field_changes.map((f) => f.field).join(", ")}</div>}
            {preview.warnings?.map((w, i) => <div key={i} className="text-xs text-amber-700">⚠ {w}</div>)}
            {preview.errors?.map((w, i) => <div key={i} className="text-xs text-red-700">✕ {w}</div>)}
            <Textarea data-testid="merge-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivazione (min 10 caratteri)" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPreview(null)} disabled={busy}>Indietro</Button>
              <Button data-testid="merge-confirm" onClick={doMerge} disabled={busy || reason.trim().length < 10 || preview.errors?.length > 0} className="bg-black hover:bg-zinc-800">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unisci e archivia doppioni"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
